import { useEffect, useState } from "react";
import { Building2, Check, Library } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../primitives/dialog";
import type { CatalogueCustomer } from "./useCatalogueContext";

export function CatalogueCustomerContext({
  customer,
  onChange,
  onCreateCustomer,
}: {
  onCreateCustomer?: () => void;
  customer: CatalogueCustomer | null;
  onChange: (customer: CatalogueCustomer | null) => void;
}) {
  const adapter = useDataAdapter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [retry, setRetry] = useState(0);
  const [results, setResults] = useState<CatalogueCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setResults([]);
    const timer = setTimeout(
      () => {
        // The customer spine, so a CIS-only customer can be picked (doc 91):
        // its catalogue code is its Tharstern code, or its CUS- code.
        adapter
          .listCisCustomers({
            q: query || undefined,
            pageSize: 50,
            isActive: true,
          })
          .then(
            (page) => {
              if (cancelled) return;
              setResults(
                page.items.map((c) => ({
                  code: c.tharstern_customer_code ?? c.customer_code,
                  name: c.name,
                })),
              );
              setLoading(false);
            },
            () => {
              if (cancelled) return;
              setFailed(true);
              setLoading(false);
            },
          );
      },
      query ? 300 : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [adapter, open, query, retry]);
  return (
    <section
      className={`flex flex-col gap-4 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${
        customer
          ? "border-primary/25 bg-primary/5"
          : "border-border bg-muted/40"
      }`}
      aria-label="Ordering customer"
    >
      <div className="flex min-w-0 items-start gap-3">
        <div
          className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-background text-primary"
          aria-hidden="true"
        >
          {customer ? (
            <Building2 className="size-5" />
          ) : (
            <Library className="size-5" />
          )}
        </div>
        <div
          className="min-w-0 space-y-1"
          aria-live="polite"
          aria-atomic="true"
        >
          <p className="text-xs font-medium text-muted-foreground">
            {customer ? "Ordering for" : "No customer selected"}
          </p>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="text-lg font-semibold break-words min-w-0">
              {customer?.name ?? "Full range"}
            </p>
            {customer && (
              <span className="text-sm font-medium text-muted-foreground">
                {customer.code}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {customer
              ? "Product availability and pricing use this customer."
              : "Standard pricing. Select a customer to see their available products and pricing."}
          </p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0 self-start sm:self-center bg-background"
        onClick={() => {
          setQuery("");
          setOpen(true);
        }}
      >
        {customer ? "Change customer" : "Select customer"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select ordering customer</DialogTitle>
            <DialogDescription>
              Search by customer name or code to check available products and
              pricing.
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label="Search by name or code"
            placeholder="Search by name or code…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div
            className="max-h-72 overflow-y-auto space-y-1"
            aria-live="polite"
          >
            {loading ? (
              <p>Loading customers…</p>
            ) : failed ? (
              <div role="alert">
                Customers could not be loaded.{" "}
                <Button
                  variant="outline"
                  onClick={() => setRetry((n) => n + 1)}
                >
                  Try again
                </Button>
              </div>
            ) : results.length === 0 ? (
              <p>No customers found.</p>
            ) : (
              results.map((result) => (
                <Button
                  key={result.code}
                  variant="ghost"
                  className="w-full h-auto min-h-10 justify-between gap-3 whitespace-normal text-left"
                  aria-pressed={customer?.code === result.code}
                  onClick={() => {
                    onChange(result);
                    setOpen(false);
                  }}
                >
                  <span className="min-w-0 break-words">{result.name}</span>
                  <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
                    {result.code}
                    {customer?.code === result.code && (
                      <Check className="size-4" aria-hidden="true" />
                    )}
                  </span>
                </Button>
              ))
            )}
          </div>
          {!customer && onCreateCustomer && (
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
                onCreateCustomer();
              }}
            >
              New customer
            </Button>
          )}
          {customer && (
            <Button
              variant="outline"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              Show the full range without a customer
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
